/* eslint-disable mastery/known-imports */
import {
  ApolloClient,
  ApolloLink,
  DataProxy,
  defaultDataIdFromObject,
  InMemoryCache,
  NormalizedCacheObject,
  Operation,
  OperationVariables,
  split,
  // eslint-disable-next-line no-restricted-imports
  useApolloClient,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { createHttpLink } from '@apollo/client/link/http';
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries';
import { WebSocketLink } from '@apollo/client/link/ws';
import { Observable, relayStylePagination } from '@apollo/client/utilities';
import {
  getErrorMessageReqIdPrefix,
  REQ_ID_KEY,
} from '@components/shared/mutation';
import { toast } from '@components/SystemAlerts/Toast';
import { LoadInfoFragment } from '@generated/fragments/loadInfo';
import { useUserEmail } from '@hooks/useUserEmail';
import { getUserIdFromStorage } from '@hooks/useUserId';
import {
  BUILD_VERSION,
  DETECT_TRANSLATIONS_IN_GQL_OPS,
  environment,
  IS_CONNECT_CARRIER,
  IS_CONNECT_CUSTOMER,
  IS_LOCAL_DEV,
  IS_LOCAL_OR_CI_CYPRESS,
  IS_NOT_PREVIEW_OR_PROD,
  IS_PREVIEW_OR_PROD,
  QUERY_PARAM_FLAGS_ENABLED,
} from '@utils/constants';
import { convertForGraphql } from '@utils/graphqlUtils';
import { jsonStringify } from '@utils/json';
import { openUrl } from '@utils/openUrl';
import { sentry } from '@utils/sentry';
import { win } from '@utils/win';
// eslint-disable-next-line mastery/known-imports
import { reportCustomError } from '@utils/reportCustomError';
import { sha256 } from 'crypto-hash';
import { GraphQLError } from 'graphql';
import { compact, debounce, get, isArray, pickBy, set } from 'lodash-es';
import { useMemo } from 'react';
import { v4 } from 'uuid';
import { config, crossAppConfig } from '../../config';
import { getLocalStorage, setLocalStorage } from '../../utils/localStorage';
import { getAuthHeader } from '../auth/token';
import {
  APP_CLIENT_THROTTLE_SETTINGS,
  APP_RELOAD_ON_AUTH_TIMEOUT_SYMBOL,
  APP_VERBOSE_ERROR_DISPLAY_SYMBOL,
} from '../GlobalVariables/constants';
import { getGlobalVariable } from '../GlobalVariables/util';
import { RESPONSE_META_KEY } from './constants';
import { getTimeoutAbortController, isAbortError } from './timeout';

const JWT_EXPIRED_CODE = 'JWT_EXPIRED_SIGNATURE';

const wsLink = new WebSocketLink({
  uri: config?.subscriptionApiEndpoint || '',
  options: {
    reconnect: true,
    lazy: true,
    connectionParams: (): anyOk => ({
      authorization: getAuthHeader(),
    }),
  },
});

const httpLink = createHttpLink({
  uri: ({ operationName, query }: Operation): string => {
    const type = get(query, 'definitions[0].operation', 'query');
    return `${crossAppConfig.apiEndpoint}?${type[0] || 'q'}=${operationName}`;
  },
});

const minionLink = createHttpLink({
  uri: ({ operationName, query }: Operation): string => {
    const type = get(query, 'definitions[0].operation', 'query') ?? '';
    return `${config.dataDictionaryEndpoint}?${
      type[0] || 'q'
    }=${operationName}`;
  },
});

const chooseEndpointLink = split(
  (operation) => {
    return operation.getContext().useMinion === true;
  },
  minionLink,
  httpLink,
);

const authLink = setContext((_, { headers }) => ({
  headers: {
    ...headers,
    authorization: getAuthHeader(),
  },
}));

const getClientOpts = (): { name: string; version: string } => {
  let name = `frontend-${environment}`;
  if (IS_CONNECT_CARRIER) {
    name = `frontend-connect-carrier-${environment}`;
  } else if (IS_CONNECT_CUSTOMER) {
    name = `frontend-connect-customer-${environment}`;
  }
  return {
    name,
    version: BUILD_VERSION as string,
  };
};

const getApolloHeaders = (): Record<string, string> => {
  const opts = getClientOpts();
  return {
    ['apollographql-client-name']: opts.name,
    ['apollographql-client-version']: opts.version,
  };
};

/**
 * Returns audit context headers for tracking request initiation.
 * These headers should be included in all GraphQL and REST API calls.
 * All values are properly stringified and null/undefined values are omitted.
 */
export const getAuditContextHeaders = (): Record<string, string> => {
  const userId = getUserIdFromStorage();
  const headers: Record<string, string> = {
    'x-mastery-audit-context-initiated-via': 'user',
    'x-mastery-audit-context-initiated-timestamp': Date.now().toString(),
  };

  // Only include user ID if it's available
  if (userId) {
    headers['x-mastery-audit-context-initiated-id'] = userId;
  }

  return headers;
};

export const getExtraHeaders = (): Record<string, Maybe<string | number>> => {
  return {
    [REQ_ID_KEY]: v4(),
    'X-Client-Version': getClientOpts().version,
    ...getAuditContextHeaders(),
    ...getApolloHeaders(),
  };
};

let connectCarrierHeaders: Record<'Company-Id' | 'Tenant-Id', string> = {
  'Company-Id': 'testId',
  'Tenant-Id': 'mm100-dev',
};

// Load headers from localStorage if available
const savedHeaders: Record<'Company-Id' | 'Tenant-Id', string> | null =
  getLocalStorage('connectCarrierHeaders');
if (savedHeaders) {
  connectCarrierHeaders = savedHeaders;
}

/** Set one or more headers for Connect Carrier Gateway. If a header is missing, the current value will be used. */
// ts-unused-exports:disable-next-line
export const setConnectCarrierHeaders = (
  headers: Partial<typeof connectCarrierHeaders>,
): void => {
  connectCarrierHeaders = { ...connectCarrierHeaders, ...headers };
  setLocalStorage('connectCarrierHeaders', connectCarrierHeaders);
};

export const getConnectCarrierHeaders = (): typeof connectCarrierHeaders => {
  return connectCarrierHeaders;
};

let connectCustomerHeaders: Record<'Company-Id' | 'Tenant-Id', string> = {
  'Company-Id': 'testId',
  'Tenant-Id': 'mm100-dev',
};

// Load headers from localStorage if available
const savedCustomerHeaders: Record<'Company-Id' | 'Tenant-Id', string> | null =
  getLocalStorage('connectCustomerHeaders');
if (savedCustomerHeaders) {
  connectCustomerHeaders = savedCustomerHeaders;
}

/** Set one or more headers for Connect Customer Gateway. If a header is missing, the current value will be used. */
// ts-unused-exports:disable-next-line
export const setConnectCustomerHeaders = (
  headers: Partial<typeof connectCustomerHeaders>,
): void => {
  connectCustomerHeaders = { ...connectCustomerHeaders, ...headers };
  setLocalStorage('connectCustomerHeaders', connectCustomerHeaders);
};

// ts-unused-exports:disable-next-line
export const getConnectCustomerHeaders = (): typeof connectCustomerHeaders => {
  return connectCustomerHeaders;
};

const customHeadersLink = setContext((_, { headers, useMinion }) => {
  // TODO: minion doesn't like extra headers, CORS errors.
  if (useMinion) {
    return headers;
  }
  return {
    headers: {
      ...headers,
      ...getExtraHeaders(),
      ...(IS_CONNECT_CARRIER ? connectCarrierHeaders : {}),
      ...(IS_CONNECT_CUSTOMER ? connectCustomerHeaders : {}),
    },
  };
});

// Timeout link for GraphQL operations
// Allows per-query timeout via context.timeout (in milliseconds)
const timeoutLink = setContext((_, previousContext) => {
  const { timeout } = previousContext;

  // If timeout is specified, create an AbortSignal with that timeout
  if (timeout && typeof timeout === 'number') {
    const controller = getTimeoutAbortController(timeout);

    return {
      fetchOptions: {
        signal: controller.signal,
      },
    };
  }

  // Otherwise, don't add a timeout
  return {};
});

const addMetaLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((data) => {
    set(data, ['data', RESPONSE_META_KEY], {
      headers: operation.getContext().headers,
    });
    return data;
  });
});

// Atlas will need to approve each of these use cases. Please provide a reason as comment.
const throttleSkipOperations = new Set([
  // @reason - AI trucks are created invidiually by single mutation. As of writing, there is no bulk create to use.
  'AICapacityCreateTruck',
  // @reason - Postings are created invidiually by single mutation in parallel. The bulk create is under development.
  'createRoutePosting',
  // @reason - Postings are updated invidiually by single mutation in parallel. The bulk update is under development.
  'updateRoutePosting',
  // @reason - Route max cost are updated invidiually by single mutation in parallel. As of writing, there is no bulk operation to use.
  'updateRouteMaxCost',
  // @reason - The available routes screen responds to locking subscription events by requesting the lock for each route. Investigation to remove this in ME-384831
  'getLock',
  // @reason - need to grab timezones of all data rows presented by AI Capacity - investigate changing this in ME-386698
  'getTimezonesForCities',
  // @reason - AI Capacity includes many different geopickers, one per row. We fire off a request per city. Attempt to fix this in ME-386751
  'getGeographiesV2',
  // @reason - Auto Truck Capacity Creates the next 7 days of auto trucks when a template is created
  'CreateTruckEntry',
  // @reason - Auto Truck Capacity Updates the next 7 days of auto trucks when a template is updated
  'updateTruckEntry',
  // @reason - (Mass Driver Assignment) Driver Assignment is done invidiually by single mutation. As of writing, there is no bulk update to use.
  'upsertRouteResources',
  // @reason - Mass booking will call request rates for each route in the list, which can be a lot of routes
  'getHistoricRateForMassBooking',
  // @reason - Recalculating route statuses with overrides is often triggered in rapid succession when completing multiple routes.
  'recalculateRouteStatusesWithOverrides',
  // @reason - Customer orders are created/updated invidiually by single mutation in parallel. The bulk create/update is not available yet.
  'createOrUpdateCustomerOrderV2',
  // @reason - This is an ad hoc query to calculate pay. This query only allows a single route in the input so tables like matches could have 10+ rows and thus 10+ calls to this query in rapid succession.
  'CalculateDriverProjectedPay',
  // @reason - Load card is used by Leo AI Chat - there may be many load cards in the same conversation.
  'getLoadCardInfo',
  // @reason - for new mass update initiative - we will be firing below stop update mutations for all selected routes in parallel in batches and need to exclude them from throttling. After https://masterysys.atlassian.net/browse/ME-457986 is done, we will not call these mutations in parallel.
  'MassUpdateCreateOrUpdateStopV2',
  'MassUpdateCreateOrUpdateCityStateStopV2',
  // @reason - Mass order status update fires this mutation for all selected orders in parallel in batches.
  'massUpdateOrderStatus',
]);

// If json flag is not set up properly, fall back to essentially no throttling
const fallbackThrottleSettings = {
  limit: 2e12,
  windowMs: 100,
} as const;

const getThrottleSettings = (): typeof fallbackThrottleSettings => {
  const throttleSettings = getGlobalVariable<typeof fallbackThrottleSettings>(
    APP_CLIENT_THROTTLE_SETTINGS,
  );
  if (throttleSettings && throttleSettings.limit && throttleSettings.windowMs) {
    return throttleSettings;
  }
  return fallbackThrottleSettings;
};

const createBlankObservable = (): anyOk => {
  // need to silently kill the operation
  return new Observable((observer) => {
    observer.complete();
  });
};

class ClientThrottleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ClientThrottleError';
  }
}

function createThrottleLink(): ApolloLink {
  const operationTimestamps: Map<string, number[]> = new Map();
  const blockedOperations = new Set<string>([]);
  const { limit, windowMs } = getThrottleSettings();

  return new ApolloLink((operation, forward) => {
    const currentOp = operation.operationName;
    if (throttleSkipOperations.has(currentOp)) {
      return forward(operation);
    }
    const now = Date.now();
    const timestamps = operationTimestamps.get(currentOp) || [];
    if (blockedOperations.has(currentOp)) {
      return createBlankObservable();
    }

    // Remove timestamps outside the sliding window
    while (timestamps.length > 0 && now - (timestamps[0] ?? 0) > windowMs) {
      timestamps.shift();
    }

    if (timestamps.length >= limit) {
      if (IS_NOT_PREVIEW_OR_PROD) {
        setTimeout(() => {
          blockedOperations.add(currentOp);
          throw new ClientThrottleError(
            `The operation ${currentOp || 'unknown'} was called ${
              timestamps.length
            } times within ${windowMs}ms. As a result, the app has been terminated. Fix the issue to unblock.`,
          );
        }, 0);
      }
      // production scenario
      reportCustomError(
        `Operation ${currentOp} was called ${timestamps.length} times within ${windowMs}ms.`,
        {
          tags: {
            team: 'atlas',
          },
        },
      );
      if (IS_PREVIEW_OR_PROD) {
        // need to silently kill the operation to avoid server pressure
        return createBlankObservable();
      }
    }

    // Add the current timestamp to the queue
    timestamps.push(now);
    operationTimestamps.set(currentOp, timestamps);

    // Clean up old timestamps periodically to avoid memory leaks
    if (operationTimestamps.size > 1000) {
      operationTimestamps.forEach((timestamps, key) => {
        if (timestamps.length === 0) {
          operationTimestamps.delete(key);
        }
      });
    }

    return forward(operation);
  });
}
interface ExtendedLoad extends LoadInfoFragment {
  truckId: string;
}

const cleanVariablesForMutationLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    operation.variables = convertForGraphql(operation.variables);
  }
  return forward(operation);
});

const skipTranslationDetectionOps = new Set([
  'CreateOrUpdateTablePreference',
  'createOrUpdateTabPreference',
  'updateOptiMatchSession',
]);

// Link to detect zero-width space character in operation variables
const detectZeroWidthSpaceLink = ((): ApolloLink => {
  const DEBOUNCE_MS = 500;
  const operationDebounceTimers: Map<string, NodeJS.Timeout> = new Map();
  const operationCollectedPaths: Map<string, Set<string>> = new Map();

  return new ApolloLink((operation, forward) => {
    if (
      DETECT_TRANSLATIONS_IN_GQL_OPS &&
      !skipTranslationDetectionOps.has(operation.operationName)
    ) {
      const ZERO_WIDTH_SPACE = '\u200B';

      const checkForZeroWidthSpace = (obj: unknown, path = ''): void => {
        if (obj === null || obj === undefined) {
          return;
        }

        if (typeof obj === 'string') {
          if (obj.includes(ZERO_WIDTH_SPACE)) {
            const msg = `Translated string detected in GraphQL operation "${
              operation.operationName
            }" at path: ${path || 'root'}. Value: "${obj}"`;

            // Always log to console for every occurrence
            // eslint-disable-next-line no-console
            console.error(msg);

            const opName = operation.operationName;

            // Collect the error path for this operation
            if (!operationCollectedPaths.has(opName)) {
              operationCollectedPaths.set(opName, new Set());
            }
            operationCollectedPaths.get(opName)?.add(path || 'root');

            // Clear any existing timer for this operation
            const existingTimer = operationDebounceTimers.get(opName);
            if (existingTimer) {
              clearTimeout(existingTimer);
            }

            // Set a new debounced timer to show toast after operation settles
            const timer = setTimeout(() => {
              const paths = operationCollectedPaths.get(opName);
              if (paths && paths.size > 0) {
                const MAX_PATHS_TO_SHOW = 5;
                const pathsArray = Array.from(paths);
                const pathsToShow = pathsArray.slice(0, MAX_PATHS_TO_SHOW);
                const remainingCount = paths.size - MAX_PATHS_TO_SHOW;
                const pathList = pathsToShow.join(', ');
                const additionalText =
                  remainingCount > 0 ? ` (and ${remainingCount} more)` : '';
                const content = `A translated string is being used in the GraphQL operation "${opName}" at ${
                  paths.size
                } path${paths.size > 1 ? 's' : ''}: ${pathList}${additionalText}.\nThis needs to be fixed as soon as possible to avoid runtime + data issues in non-English locales.`;
                toast({
                  title: 'Translation Issue',
                  content,
                  type: 'error',
                  action: {
                    label: 'Learn More',
                    onClick: () => {
                      openUrl(
                        'https://masterysys.atlassian.net/wiki/x/ogLaBAE',
                      );
                    },
                  },
                  toastOptions: {
                    autoClose: false,
                  },
                });

                // Clean up after showing toast
                operationCollectedPaths.delete(opName);
                operationDebounceTimers.delete(opName);
              }
            }, DEBOUNCE_MS);

            operationDebounceTimers.set(opName, timer);
          }
          return;
        }

        if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            checkForZeroWidthSpace(item, `${path}[${index}]`);
          });
          return;
        }

        if (typeof obj === 'object') {
          Object.entries(obj).forEach(([key, value]) => {
            const newPath = path ? `${path}.${key}` : key;
            checkForZeroWidthSpace(value, newPath);
          });
        }
      };

      if (operation.variables) {
        checkForZeroWidthSpace(operation.variables);
      }
    }

    return forward(operation);
  });
})();

const ERROR_AND_RELOAD_TIMEOUT_MS = 1000 * 10;

const errorAndReloadDebounced = debounce(
  () => {
    if (getGlobalVariable(APP_RELOAD_ON_AUTH_TIMEOUT_SYMBOL)) {
      const timeout = setTimeout(
        () => win.location.reload(),
        ERROR_AND_RELOAD_TIMEOUT_MS,
      );
      toast({
        content: `Your browser window has been open too long, refreshing in ${Math.round(
          ERROR_AND_RELOAD_TIMEOUT_MS / 1000,
        )} seconds...`,
        type: 'error',
        toastOptions: {
          onClose: () => clearTimeout(timeout),
          autoClose: false,
        },
      });
    }
  },
  200,
  // we want to fire off this event immediately, not wait until things settle
  // network requests might keep firing, so take the first invocation
  { leading: true, trailing: false },
);

interface MasteryGraphQLError extends Omit<GraphQLError, 'extensions'> {
  extensions: {
    code?: string;
    details?: string;
    serviceName?: string;
    exception?: {
      stacktrace?: string[];
    };
  };
}

const getErrorMessage = (message?: string): string => {
  const isInputError = (message || '').startsWith(
    'Variable "$input" got invalid value',
  );
  if (isInputError) {
    return 'Invalid input variable';
  }
  return message || 'Unknown';
};

const jwtExpiredTrace =
  'AuthenticationError: Context creation failed: Invalid JWT: TokenExpiredError: jwt expired.';

// Mostly built-in graphql error codes that highlight an issue with the FE request to BE
// https://www.apollographql.com/docs/apollo-server/data/errors/#built-in-error-codes
const optInCodes = new Set([
  'GRAPHQL_PARSE_FAILED',
  'GRAPHQL_VALIDATION_FAILED',
  'PERSISTED_QUERY_NOT_SUPPORTED',
  'OPERATION_RESOLUTION_FAILURE',
  'BAD_USER_INPUT',
]);

// Backend business-rule validation errors that happen to use BAD_USER_INPUT code.
// These are not frontend bugs and should not be reported to Sentry.
const businessLogicErrorPatterns: RegExp[] = [
  /outside of tolerance/i,
  /max cost of .* will be less than/i,
  /must be in the .* financial status/i,
  /currency mismatch/i,
  /no active hold for/i,
  /batch contained no valid/i,
  /already in use/i,
  /higher priority .* already exists/i,
  /approved amount .* will cause the sum/i,
  /intended for deletion does not exist/i,
  /reason is invalid/i,
  /Database Validation Error/i,
  /_response: \{/i,
];

const isBusinessLogicError = (message: string): boolean =>
  businessLogicErrorPatterns.some((pattern) => pattern.test(message));

const errorLink = onError(({ graphQLErrors, operation, networkError }) => {
  const type = get(operation.query, 'definitions[0].operation', 'query');
  const opName = operation.operationName;
  // For some reason, graphQLErrors will pass the isArray function, but will not have a map method...
  // https://sentry.io/organizations/mastery-logistics-systems/issues/1901239299
  if (isArray(graphQLErrors)) {
    const errorsArr = graphQLErrors as MasteryGraphQLError[];
    errorsArr.forEach((errorObj) => {
      try {
        const { message, locations, path, extensions } = errorObj;
        const { code, details, serviceName } = extensions || {};
        const firstStackTrace =
          get(extensions, 'exception.stacktrace[0]') || '';
        if (code === JWT_EXPIRED_CODE || firstStackTrace === jwtExpiredTrace) {
          errorAndReloadDebounced();
          return;
        }

        if (!optInCodes.has(code || '')) {
          return;
        }

        // Skip business-logic validation errors for BAD_USER_INPUT — these
        // are backend domain rules, not frontend bugs (e.g.
        // "outside of tolerance", "currency mismatch").
        if (code === 'BAD_USER_INPUT' && isBusinessLogicError(message || '')) {
          return;
        }

        let exceptionData: anyOk;
        try {
          exceptionData = {
            tags: pickBy({
              service: serviceName || '',
              code: code || '',
              graphql: 'true',
              path: path?.join('.') || '',
              operationType: type,
              operation: opName,
            }),
            extra: pickBy({
              locations: jsonStringify(locations),
              details,
              message,
            }),
          };
        } catch {
          // noop
        }

        // https://blog.sentry.io/2019/01/17/debug-tough-front-end-errors-sentry-clues#group-errors-your-way-with-fingerprints
        sentry.withScope((scope) => {
          scope.setFingerprint([
            serviceName,
            code,
            type,
            opName,
            firstStackTrace,
          ]);
          reportCustomError(new Error(getErrorMessage(message)), exceptionData);
        });
      } catch (outerErr) {
        reportCustomError(outerErr as Error);
      }
    });
  } else if (graphQLErrors) {
    reportCustomError(jsonStringify(graphQLErrors));
  }

  // This code propagates a better message to the user with enhanced context.
  // Apollo client treats network errors differently, and as of writing makes it difficult to pass along custom data with the error. So we encode our info in the error message directly.
  if (networkError?.message) {
    if (isAbortError(networkError)) {
      // silence timeout errors (handled by timeoutLink) and avoid further processing
      return;
    }
    if (!getGlobalVariable(APP_VERBOSE_ERROR_DISPLAY_SYMBOL)) {
      networkError.message =
        'There was an error processing your request. An automatic report has been generated.';
    } else {
      const prefix = getErrorMessageReqIdPrefix(
        operation.getContext() as anyOk,
      );
      const firstErr = get(graphQLErrors, '0.message');
      const finalMessage = firstErr || networkError.message;
      networkError.message = `${prefix}${finalMessage}`;
    }
  }
});

const persistedQueriesLink = createPersistedQueryLink({
  sha256,
});

// Used in cypress component test rendering
// ts-unused-exports:disable-next-line
export const getClient = (kwargs: {
  persistedQueries: boolean;
  connectCarrier?: boolean;
}): ApolloClient<NormalizedCacheObject> => {
  const link = ApolloLink.from(
    compact([
      kwargs.persistedQueries ? persistedQueriesLink : undefined,
      cleanVariablesForMutationLink,
      detectZeroWidthSpaceLink,
      authLink,
      customHeadersLink,
      createThrottleLink(),
      addMetaLink,
      errorLink,
      timeoutLink,
      chooseEndpointLink,
    ]),
  );

  const splitLink = split(
    // split based on operation type
    (op) => {
      const isSubscription = !!op.query.definitions.find(
        (def) =>
          def.kind === 'OperationDefinition' &&
          def.operation === 'subscription',
      );
      return isSubscription;
    },
    wsLink,
    link,
  );

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          allAvailableRoutes: relayStylePagination(),
          allAvailableTrucks: relayStylePagination(['filter', 'routeId']),
          allAvailableGateReservations: relayStylePagination(),
          allAvailableContainers: relayStylePagination(),
          allFacilityEquipmentCapacities: relayStylePagination(),
          matches: relayStylePagination(),
          gateReservationMatches: relayStylePagination(),
          containerMatches: relayStylePagination(),
          resourceTimelineDriverConnection: relayStylePagination([
            'filters',
            'dateRange',
            'eventTypes',
            'sort',
          ]),
          getContainersForCarrier: relayStylePagination(),
          getGateReservationsForCarrier: relayStylePagination(),
          getTruckEntriesForCarrier: relayStylePagination(),
          getTruckEntries: relayStylePagination(),
          customerCommitments: relayStylePagination(),
          clientExceptionRecords: relayStylePagination(),
          incidentsV2: relayStylePagination(),
          tasksPaginatedV2: relayStylePagination(),
          carrierCommitments: relayStylePagination(),
          searchMatchingRoutesForTruck: relayStylePagination(),
          searchMatchingRoutesForContainer: relayStylePagination(),
          searchMatchingTrucksForRoute: relayStylePagination(),
          searchMatchingGateReservationsForRoute: relayStylePagination(),
          searchMatchingContainersForRoute: relayStylePagination(),
          allEdiManagementTransactions: relayStylePagination(),
          allLqsRoutes: relayStylePagination(['filter']),
        },
      },
      EmployeeAdditionalDivisions: {
        keyFields: ['id', 'employeeId'],
      },
      // The same recipient id appears in the warning/started/ended sections
      // with different notificationTime values; normalizing by id lets the
      // null times overwrite the warning time, so embed in the parent instead.
      DetentionNotificationRecipient: {
        keyFields: false,
      },
      CustomerSearch: {
        keyFields: ['id', 'customerAddressId'],
      },
      CarrierSearch: {
        keyFields: ['id', 'carrierAddressId'],
      },
      SeerMainPageRouteBoardDetails: {
        keyFields: ['routeNumber'],
      },
      // `id` is the load id and repeats for each stop in detention.
      SeerDetentionQueue: {
        keyFields: ['stopId'],
      },
      FacilitySearch: {
        keyFields: ['id', 'address', 'state'],
      },
      SeerMainPageTrackingBoard: {
        keyFields: ['routeNumber'],
      },
      AccountingUnvoucheredCarrierProcessingQueue: {
        keyFields: ['routeId', 'vendorId'],
      },
      OptimalMatchesMatchedDriverPreferences: {
        keyFields: ['id', 'routeId'],
      },
      SeerFacilityTrackingBoard: {
        keyFields: ['routeNumber', 'destination'],
      },
      SeerTrackingPage: {
        keyFields: ['routeNumber'],
      },
      TrackingPage: {
        keyFields: ['routeNumber'],
      },
      MainPageTrackingBoard: {
        keyFields: ['routeNumber'],
      },
      FacilityTracking: {
        keyFields: ['routeNumber'],
      },
      StopAddress: {
        keyFields: ['id', 'city'],
      },
      // id is nullable; normalizing would collapse every null-id address into
      // a single shared entity, so embed those in the parent RouteStopV2.
      // merge keeps queries selecting different fields of the same embedded
      // address from replacing (and so evicting) each other's fields.
      StopAddressV2: {
        keyFields: (obj): false | string[] => (obj.id == null ? false : ['id']),
        merge: true,
      },
      SeerLoadSearch: {
        keyFields: ['routeId'],
      },
      SeerCarrierRoute: {
        keyFields: ['routeId'],
      },
      CustomerCustomerRelationship: {
        keyFields: ['id', 'customerId'],
      },
      SeerCustomerOrder: {
        keyFields: ['routeId'],
      },
      SeerFacilityRoute: {
        keyFields: ['routeId'],
      },
      CarrierCarrierRelationship: {
        keyFields: ['id', 'carrierId'],
      },
      BidConnection: {
        merge: true,
      },
      BidLaneConnection: {
        merge: true,
      },
      AvailableRoute: {
        merge: true,
      },
      MarkupPagedResponse: {
        merge: true,
      },
      PagedResponse: {
        merge: true,
      },
      AvailableTruck: {
        keyFields: ['truckPostingId'],
      },
      AvailableRouteDivision: {
        keyFields: ['routeId', 'id'],
      },
      CrmOpportunityOutputV2: {
        keyFields: ['opportunityId', 'entityId'],
      },
      CrmOpportunityBusinessUnitOutputV2: {
        keyFields: ['opportunityBusinessUnitId'],
      },
      crmOpportunityContactOutput: {
        keyFields: ['Opportunity_Contact_Id', 'Contact_Id'],
      },
      crmOpportunityEquipmentOutput: {
        keyFields: ['Opportunity_Equipment_Id', 'Opportunity_Id'],
      },
      crmOpportunityIbRegionOutput: {
        keyFields: ['Opportunity_IBRegion_Id', 'Opportunity_Id'],
      },
      crmOpportunityModeOutput: {
        keyFields: ['Opportunity_Mode_Id', 'Opportunity_Id'],
      },
      crmOpportunityObRegionOutput: {
        keyFields: ['Opportunity_OBRegion_Id', 'Opportunity_Id'],
      },
      CrmOpportunityRepsOutputV2: {
        keyFields: ['opportunityRepId', 'opportunityId'],
      },
      crmOpportunitySizeOutput: {
        keyFields: ['Opportunity_Size_Id', 'Opportunity_Id'],
      },
      crmOpportunitySolutionOutput: {
        keyFields: ['Opportunity_Solution_Id', 'Opportunity_Id'],
      },
      SeerMainPageRouteBoardPending: {
        keyFields: ['orderId'],
      },
      GeographyServiceRecord: {
        keyFields: ['sourceId'],
      },
      OptiMatchSession: {
        keyFields: ['sessionId'],
      },
      OptiMatchSolution: {
        keyFields: ['sessionId'],
      },
      OptiMatchDriver: {
        keyFields: ['capacityId'],
        fields: {
          availableTruck: {
            merge(existing, incoming): anyOk {
              return incoming ?? existing;
            },
          },
        },
      },
      OptiMatchRoute: {
        fields: {
          availableRoute: {
            merge(existing, incoming): anyOk {
              return incoming ?? existing;
            },
          },
        },
      },
      MainPageRouteBoardOpen: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardArrivedAtDestination: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardArrivedAtOrigin: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardCompleted: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardCovered: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardDispatched: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardInTransit: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardPending: {
        keyFields: ['orderId', 'routeId'],
      },
      MainPageRouteBoardIncomplete: {
        keyFields: ['orderId', 'routeId'],
      },
      MainPageRouteBoardPreTender: {
        keyFields: ['routeNumber'],
      },
      MainPageRouteBoardTender: {
        keyFields: ['routeNumber'],
      },
      VoucheredDrivers: {
        keyFields: ['id', 'voucherLoadRoute'],
      },
      TruckEntryDriver: {
        keyFields: ['truckEntryId', 'name', 'id'],
      },
      DriverRelationshipOutput: {
        keyFields: ['id', 'driverId'],
      },
      RouteMaxCostType: {
        keyFields: ['routeId'],
      },
    },
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    dataIdFromObject: (obj) => {
      const { __typename: n } = obj;
      if (n === 'calculateVatCheckedModel') {
        // this query/type is at a tenant level, there is no need for id
        // but to use optimistic options from apollo client we need to generate an identifier
        return 'calculateVatCheckedModel:tenant';
      } else if (n === 'TempLoad') {
        // we shouldn't have to do this if the ID for the TempLoad object is distinct per Truck + Load combo.
        // As of now, it is not - and loads with the same id, but different DDH or ODH values happen, and the cache gets confused
        // So we stabilize the cache by narrowing down the exact id ourselves
        const load = obj as unknown as ExtendedLoad;
        try {
          // this load.truck access is dependent on adding the truck object in the response above.
          return `TempLoad:${load.id}-TruckPosting:${load.truckId}`;
        } catch {
          // hopefully this never happens, but if it does then we are effectively bailing out of the cache as an escape hatch
          return `TempLoad:${Math.random().toString()}`;
        }
      } else if (n === 'RouteVendorCost') {
        const routeId = obj.routeId || obj.routeIdAsNullable;
        const vendorId = obj.vendorId || obj.vendorIdAsNullable;
        if (routeId && vendorId) {
          return `RouteVendorCost:${routeId}-${vendorId}`;
        }
      } else if (n === 'CarrierConnectRoutesNode') {
        // routeId is the unique route identifier, but it's nullable.
        // Use routeId if available, fall back to id to prevent collisions on null values.
        const routeId = obj.routeId;
        const id = obj.id;
        if (routeId) {
          return `CarrierConnectRoutesNode:${routeId}`;
        }
        if (id) {
          return `CarrierConnectRoutesNode:${id}`;
        }
      }
      return defaultDataIdFromObject(obj);
    },
    possibleTypes: {
      LoadTender: ['DrayNotification', 'RailBilling', 'RateConfirmationV2'],
      ResourceTimelineDriverEvent: [
        'ResourceTimelineDriverEventCapacity',
        'ResourceTimelineDriverEventRoute',
        'ResourceTimelineDriverEventRouteEMR',
        'ResourceTimelineDriverEventRouteTimeOff',
        'ResourceTimelineDriverEventRoutingRequest',
      ],
    },
  });

  return new ApolloClient({
    link: splitLink,
    cache,
    ...getClientOpts(),
    connectToDevTools: QUERY_PARAM_FLAGS_ENABLED,
    defaultOptions: {
      query: {
        errorPolicy: 'all',
      },
    },
  });
};

const persistedQueriesOptOut = new Set(['keycloaksuperuser@mastery.net']);

interface ClientOpts {
  connectCarrier?: boolean;
  connectCustomer?: boolean;
}

export const useMasteryApolloClient = (
  kwargs?: ClientOpts,
): ApolloClient<NormalizedCacheObject> => {
  const email = useUserEmail();
  const emailInOptOut = persistedQueriesOptOut.has(
    email?.toLowerCase() ?? 'noop',
  );
  const optOutOfPersistedQueries =
    emailInOptOut || IS_LOCAL_DEV || IS_LOCAL_OR_CI_CYPRESS;
  return useMemo(() => {
    return getClient({
      persistedQueries: optOutOfPersistedQueries ? false : true,
      ...kwargs,
    });
  }, [emailInOptOut]);
};

export const useFragmentReader = (): (<T, TVariables = OperationVariables>(
  options: DataProxy.Fragment<TVariables, T>,
  optimistic?: boolean,
) => T | null) => {
  const client = useApolloClient();
  return (options, optimistic) => client.readFragment(options, optimistic);
};
